import React from 'react';
import {
  Badge,
  Button,
  EmptyState,
  Flex,
  Markdown,
  Spinner,
  TagBadge,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Copy, DownloadSimple, Check, PencilSimple, Trash, Sparkle } from '@phosphor-icons/react';
import { useDeletePrompt, usePrompt, type PromptDetail } from '../api';

/** Trigger a browser download of `content` as `<slug>.md`. */
function downloadMarkdown(slug: string, content: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slug}.md`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

type Props = {
  promptId: string;
  /** Whether the viewer may edit/delete this prompt. */
  canMutate: (prompt: PromptDetail) => boolean;
  onEdit: (prompt: PromptDetail) => void;
  /** Called after a successful delete so the parent can clear the selection. */
  onDeleted: () => void;
};

/**
 * Main-pane read view for a prompt: a compact header with copy / download /
 * edit / delete affordances, the prompt's tags, and the rendered markdown body.
 * Replaces the old `PromptDetailDialog` modal.
 */
export function PromptViewer({ promptId, canMutate, onEdit, onDeleted }: Props) {
  const { toast } = useToast();
  const { data, isLoading, isError } = usePrompt(promptId);
  const deletePrompt = useDeletePrompt();
  const [copied, setCopied] = React.useState(false);
  const prompt = data?.data;

  React.useEffect(() => setCopied(false), [promptId]);

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      toast.success('Prompt copied');
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (!prompt) return;
    try {
      await deletePrompt.mutateAsync(prompt.id);
      toast.success('Prompt deleted');
      onDeleted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete prompt');
    }
  };

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className="h-full">
        <Spinner />
      </Flex>
    );
  }

  if (isError || !prompt) {
    return (
      <Flex align="center" justify="center" className="h-full">
        <EmptyState
          icon={<Sparkle size={24} />}
          title="Prompt not found"
          description="It may have been deleted, or you don't have access to it."
        />
      </Flex>
    );
  }

  const mutable = canMutate(prompt);

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-border px-8 py-5">
        <Flex align="start" justify="between" gap="md">
          <div className="min-w-0">
            <Text size="xs" weight="medium" className="mb-1 block uppercase tracking-wide text-muted-foreground">
              {prompt.scope === 'global' ? 'Cofactor prompt' : 'Prompt'}
            </Text>
            <Text size="xl" weight="semibold" className="block text-foreground">
              {prompt.title}
            </Text>
            {prompt.description && (
              <Text size="sm" className="mt-1 block text-muted-foreground">
                {prompt.description}
              </Text>
            )}
          </div>
          <Flex align="center" gap="sm" className="shrink-0 flex-wrap justify-end">
            <Button variant="secondary" size="sm" onPress={handleCopy}>
              <Flex align="center" gap="xs">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied' : 'Copy'}
              </Flex>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onPress={() => downloadMarkdown(prompt.slug, prompt.content)}
            >
              <Flex align="center" gap="xs">
                <DownloadSimple size={16} />
                Download
              </Flex>
            </Button>
            {mutable && (
              <>
                <Button variant="ghost" size="sm" onPress={() => onEdit(prompt)}>
                  <Flex align="center" gap="xs">
                    <PencilSimple size={16} />
                    Edit
                  </Flex>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={handleDelete}
                  isDisabled={deletePrompt.isPending}
                >
                  <Flex align="center" gap="xs">
                    <Trash size={16} />
                    Delete
                  </Flex>
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {(prompt.scope === 'global' || prompt.tags.length > 0) && (
          <Flex align="center" gap="xs" className="mt-3 flex-wrap">
            {prompt.scope === 'global' && <Badge variant="secondary">Cofactor</Badge>}
            {prompt.tags.map((tag) => (
              <TagBadge key={tag.id ?? tag.name} name={tag.name} color={tag.color} size="sm" />
            ))}
          </Flex>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <Markdown className="prose prose-sm max-w-none text-foreground">{prompt.content}</Markdown>
      </div>
    </div>
  );
}
